import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowUp, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignQuoteSection, { useCampaignQuoteSectionForm } from '@hooks/useCampaignQuoteSection'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ToggleListItem from '@components/ToggleListItem'

const MODAL_KEY = 'CreateOrEditCampaignQuoteSectionModal'

type MoveSectionParams = {
  callbacks: { closeModal: () => void },
  campaignQuoteSectionIds: number[],
  direction: string,
  fromIndex: number,
  moveFn: Function,
}

const moveSection = (params: MoveSectionParams) => {
  const {
    callbacks, campaignQuoteSectionIds, direction, fromIndex, moveFn,
  } = params
  const { closeModal } = callbacks

  const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1

  moveFn({ campaignQuoteSectionIds, fromIndex, toIndex }).then(() => {
    // Need to close the modal so that when can reset the index when editing the section again
    closeModal()
  })
}

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignQuoteSection: (payload: HandleCallbackActionParams) => void,
  updateCampaignQuoteSection: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignQuoteSectionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignQuoteSection, updateCampaignQuoteSection } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    index, first, last, campaignQuoteSectionIds,
  } = modalPayload

  const campaignQuoteSection = digObject(modalPayload, 'campaignQuoteSection', {})

  const newRecord = !campaignQuoteSection.id

  const {
    callbacks: {
      createCampaignQuoteSection: createFn,
      deleteCampaignQuoteSection: deleteFn,
      moveQuoteSectionId: moveFn,
      updateCampaignQuoteSection: updateFn,
    },
    creating,
    updating,
  } = useCampaignQuoteSection(campaignQuoteSection)

  const {
    entityState,
    entityState: {
      footer, header, hide_dates, hide_prices, show_subtotal, title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCampaignQuoteSectionForm(campaignQuoteSection, { validateOn: 'change' })

  const createOrUpdateCampaignQuoteSectionPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: ` Quote Section ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine=" Quote Section"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Header" errorMessage={errors.header} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="header" value={header} style={{ height: 80 }} {...handlers} />
          </Form.Field>

          <Form.Field label="Footer" errorMessage={errors.footer} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="footer" value={footer} style={{ height: 80 }} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}> PDF Settings</SectionDivider>

          <ToggleListItem
            onClick={() => setEntityState({ hide_dates: !hide_dates })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={hide_dates}
          >
            <ToggleListItem.Title>Hide Dates</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled, item dates will not be visible on the downloadable PDF.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ hide_prices: !hide_prices })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={hide_prices}
          >
            <ToggleListItem.Title>Hide Prices</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled, item prices will not be visible on the downloadable PDF.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ show_subtotal: !show_subtotal })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={show_subtotal}
          >
            <ToggleListItem.Title>Show Subtotal</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled, section subtotal will be shown on the downloadable PDF.
            </ToggleListItem.Description>
          </ToggleListItem>

          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              onClick={() => confirmDelete({
                callbacks: {
                  afterAction: closeModal,
                  deleteFn,
                },
                strings: {
                  entityName: 'Quote Section',
                },
              })}
              marginTop="medium"
              size="medium"
            >
              Delete Section
            </Button>
          </Form.Field>

          {!(first && last) && (
            <Form.Field label="Position" marginTop="large">
              {!first && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faArrowUp} />}
                  onClick={() => moveSection({
                    callbacks,
                    campaignQuoteSectionIds,
                    direction: 'up',
                    fromIndex: index,
                    moveFn,
                  })}
                  size="medium"
                >
                  Move Section Up
                </Button>
              )}

              {!last && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faArrowDown} />}
                  marginTop="medium"
                  onClick={() => moveSection({
                    callbacks,
                    campaignQuoteSectionIds,
                    direction: 'down',
                    fromIndex: index,
                    moveFn,
                  })}
                  size="medium"
                >
                  Move Section Down
                </Button>
              )}
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignQuoteSection(createOrUpdateCampaignQuoteSectionPayload)
              : () => updateCampaignQuoteSection(createOrUpdateCampaignQuoteSectionPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'}  Quote Section` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignQuoteSectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
